import * as ko from "knockout";

import { PliCrumbs } from "../forms/forms";


const safeIdRegEx = new RegExp("^\\.(?:\\(\\d*\\))?$");

declare module "knockout" {

    /**
     * Extend knockout binding handlers to include breadcrumbs.
     */
    interface BindingHandlers {
        pliBreadcrumbs: { update: (element: any, valueAccessor: () => Breadcrumb[]) => void };
    }
}


/**
 * Defines a custom knockout binding for breadcrumbs.
 */
ko.bindingHandlers.pliBreadcrumbs = {
    /**
     * Modifies the element by adding a list element for breadcrumbs.
     *
     * @param element
     *     The HTML element that the knockout binding is applied to.
     * @param valueAccessor
     *     An array containing breadcrumb segments.
     */
    update(element: any, valueAccessor: () => Breadcrumb[]): void {
        let html = "";
        const crumbs = ko.unwrap(valueAccessor());
        if (crumbs && crumbs.length > 0) {
            const l = crumbs.length;
            for (let i = 0; i < l; i++) {
                if (typeof crumbs[i].name !== "string" || typeof crumbs[i].href !== "string") {
                    throw new Error("pliBreadcrumbs.update: each crumb requires a 'name' and 'href'.");
                }

                html = (i === l - 1)  // last item (exclude href + add active class)
                    ? html.concat("<li class=\"active\"><a>")
                    : html.concat("<li><a href=\"").concat(crumbs[i].href).concat("\">");
                html = html.concat(crumbs[i].name).concat("</a></li>");
            }
        }
        if (html !== "" && element) { element.innerHTML = html; }
    }
};


/**
 * Recursively find the display ID for the item with the given safe ID.
 *
 * This must be recursive because we always start at the page
 * viewmodel level.
 *
 * @param safeId
 *     The value which should match a safeId property value on the given model's
 *     extra object or from a child property' extra object.
 * @param modelObject
 *     The model object to look on for a display ID.
 * @returns
 *     The display ID on the model object or null if not found.
 */
function findDisplayId(safeId: string, modelObject: any): string {
    // If no model object is given, revert to old behaviour (return null)
    if (!modelObject) {
        return null;
    }

    // If the model object has a defined safeID and it matches
    // use either the property specified by displayIdObservable
    // or extras.displayId
    if (modelObject.extras && ko.isObservable(modelObject.extras.safeId) && modelObject.extras.safeId() === safeId) {
        if (modelObject.displayIdObservable && ko.isObservable(modelObject[modelObject.displayIdObservable])) {
            return modelObject[modelObject.displayIdObservable]();
        }
        if (ko.isObservable(modelObject.extras.displayId)) {
            return modelObject.extras.displayId();
        }
    }

    // If the safeId doesn't match, one of our children might have
    // that safeId, so check all arrays for view models and try
    // recursively.
    for (let prop in modelObject) {
        if (modelObject.hasOwnProperty(prop) && modelObject[prop] && modelObject[prop].removeAll) {
            const array = modelObject[prop]();
            for (let i = 0; i < array.length; i++) {
                const displayId = findDisplayId(safeId, array[i]);
                if (displayId) {
                    return displayId;
                }
            }
        }
    }

    return null;
}


/**
 * A segment for a breadcrumb.
 */
export interface Breadcrumb {
    name: string;
    href: string;
}


/**
 * Creates an observable containing an array of breadcrumb navigation links.
 *
 * Changes to the crumbs observable will modify the contents of the breadcrumbs
 * computed observable.
 *
 * @param crumbs
 *     The navigation objects to create bredcrumbs from.
 * @param viewModel
 *     An optional viewmodel used in creating a breadcrumb title if it contains a safe ID property.
 * @returns
 *     A read-only observable containing an array of breadcrumbs constructed from the crumbs observable.
 */
export function createBreadcrumbs(crumbs: ko.Observable<PliCrumbs>, viewModel?: any): ko.PureComputed<Breadcrumb[]> {
    if (!ko.isObservable(crumbs)) {
        throw new Error("createBreadcrumbs: \"crumbs\" must be an observable.");
    }

    return ko.pureComputed(() => {
        const result: Breadcrumb[] = [];
        const crumbsValue = crumbs();
        if (crumbsValue) {
            let currentCrumb = crumbsValue.begin;
            while (currentCrumb) {
                if (currentCrumb.page && currentCrumb.page.form && !currentCrumb.inLocationHash) { break; }

                const title = (result.length === 0
                    ? ""
                    : (currentCrumb.page && currentCrumb.page.title)
                    || (currentCrumb.data && viewModel && findDisplayId(currentCrumb.data.id, viewModel))
                    || (currentCrumb.data && safeIdRegEx.test(currentCrumb.data.id) ? "(New)" : currentCrumb.data.id));

                result.push({ name: title, href: currentCrumb.hash });
                currentCrumb = currentCrumb.next;
            }
        }

        return result;
    });
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/forms/breadcrumbs.ts